class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = (event) => {
      // Invalidate a response as soon as another choice is made, before the
      // debounce expires, so it cannot replace an unchecked/checked input.
      FacetFiltersForm.cancelPendingUpdate();
      FacetFiltersForm.submitTimeout = setTimeout(() => {
        FacetFiltersForm.submitTimeout = null;
        this.onSubmitHandler(event);
      }, 800);
    };

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', (event) => {
      if (event.target.matches('[data-meroo-catalog-bundle]')) {
        facetForm.querySelectorAll('[data-meroo-catalog-prep]').forEach((input) => {
          input.checked = event.target.checked;
        });
      }
      FacetFiltersForm.syncCatalogSelections(facetForm);
      // Range handles repaint immediately; submit only a committed drag or exact price.
      if (event.target.matches('[data-meroo-price-bound], [data-meroo-price-edit]')) return;
      this.debouncedOnSubmit(event);
    });
    facetForm.addEventListener('change', (event) => {
      if (event.target.matches('[data-meroo-price-bound], [data-meroo-price-edit]')) {
        this.debouncedOnSubmit(event);
      }
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
    FacetFiltersForm.syncCatalogSelections(facetForm);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (
        searchParams === FacetFiltersForm.searchParamsPrev &&
        window.location.pathname === FacetFiltersForm.pathnamePrev &&
        !FacetFiltersForm.submitTimeout
      ) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true, pathname = window.location.pathname) {
    FacetFiltersForm.cancelPendingUpdate();
    document.getElementById('FacetUpdateError')?.remove();
    const requestId = FacetFiltersForm.requestId;
    const controller = new AbortController();
    FacetFiltersForm.abortController = controller;
    FacetFiltersForm.searchParamsPrev = searchParams;
    FacetFiltersForm.pathnamePrev = pathname;
    const sections = FacetFiltersForm.getSections();
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, updateEvent, requestId)
        : FacetFiltersForm.renderSectionFromFetch(url, event, updateEvent, requestId, controller.signal);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams, pathname);
  }

  static cancelPendingUpdate() {
    clearTimeout(FacetFiltersForm.submitTimeout);
    FacetFiltersForm.submitTimeout = null;
    FacetFiltersForm.requestId += 1;
    FacetFiltersForm.abortController?.abort();
    FacetFiltersForm.abortController = null;
  }

  static syncCatalogSelections(form) {
    const bundle = form.querySelector('[data-meroo-catalog-bundle]');
    if (!bundle) return;
    const inputs = Array.from(form.querySelectorAll('[data-meroo-catalog-prep]'));
    const selected = inputs.filter((input) => input.checked).length;
    bundle.checked = inputs.length > 0 && selected === inputs.length;
    bundle.indeterminate = selected > 0 && selected < inputs.length;
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');
    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';
    const currentCount = parseInt(document.getElementById('ProductCount')?.dataset.productCount) || 0;
    const urlSearchParams = new URLSearchParams(searchParams);
    const isSearchPage = facetsContainer?.dataset.template === 'search';
    const isCollectionPage = facetsContainer?.dataset.template === 'collection';
    const dispatchTarget = facetsForm || document;

    let deferred;

    if (isSearchPage) {
      deferred = SearchUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
            sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
          },
          promise: deferred.promise,
        }),
      );
    } else if (isCollectionPage) {
      // collectionId is null for auto-generated collections (/collections/all,
      // /collections/vendors/<x>, etc.), the SSE schema accepts that.
      deferred = CollectionUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: { id: collectionId, handle: collectionHandle, productsCount: currentCount },
          productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
          promise: deferred.promise,
        }),
      );
    }

    if (!deferred) return;

    return {
      resolve: (filteredCount) => {
        deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount });
      },
      reject: deferred.reject,
    };
  }

  static renderSectionFromFetch(url, event, updateEvent, requestId, signal) {
    return fetch(url, { signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Unable to update filters (${response.status})`);
        return response.text();
      })
      .then((html) => {
        if (requestId !== FacetFiltersForm.requestId) {
          updateEvent?.reject(new DOMException('A newer filter selection replaced this request.', 'AbortError'));
          return;
        }
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderSection(html, event, updateEvent);
      })
      .catch((error) => {
        if (error.name !== 'AbortError' && requestId === FacetFiltersForm.requestId) {
          console.error(error);
          FacetFiltersForm.recoverFailedUpdate();
        }
        updateEvent?.reject(error);
      });
  }

  static recoverFailedUpdate() {
    const grid = document.getElementById('ProductGridContainer');
    grid?.querySelector('.collection')?.classList.remove('loading');
    ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
      document.getElementById(id)?.classList.remove('loading');
    });
    document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach((spinner) => spinner.classList.add('hidden'));
    FacetFiltersForm.toggleActiveFacets(false);
    if (!grid || document.getElementById('FacetUpdateError')) return;
    const message = document.createElement('p');
    message.id = 'FacetUpdateError';
    message.className = 'facets__error';
    message.setAttribute('role', 'alert');
    message.textContent = 'Products could not be updated. The previous results are still shown. ';
    const retry = document.createElement('a');
    retry.href = `${FacetFiltersForm.pathnamePrev}?${FacetFiltersForm.searchParamsPrev}`;
    retry.className = 'link underlined-link';
    retry.textContent = 'Try again';
    message.append(retry);
    grid.prepend(message);
  }

  static renderSectionFromCache(filterDataUrl, event, updateEvent, requestId) {
    if (requestId !== FacetFiltersForm.requestId) return;
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event, updateEvent);
  }

  static renderSection(html, event, updateEvent) {
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html, updateEvent);
    // Client product-type filter must run after the grid + count are replaced.
    // Search & Discovery often returns the full collection for filter.p.product_type.
    FacetFiltersForm.restoreMerooCatalogState(window.location.search);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html, updateEvent) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHtml.getElementById('ProductCount');
    const count = sourceCount.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    const productCount = sourceCount.dataset.productCount || '';
    const totalCount = sourceCount.dataset.totalCount || '';
    container.innerHTML = count;
    container.dataset.productCount = productCount;
    container.dataset.totalCount = totalCount;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.dataset.productCount = productCount;
      containerDesktop.dataset.totalCount = totalCount;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    updateEvent?.resolve(parseInt(sourceCount.dataset.productCount) || 0);
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (!isTextInput) {
          // Try to return focus to the same checkbox the user just toggled,
          // re-selecting it from the freshly rendered HTML by its id.
          const originatingInputId = event.target.id;
          const matchingInput = originatingInputId
            ? newFacetDetailsElement.querySelector(`#${CSS.escape(originatingInputId)}`)
            : null;

          if (matchingInput) {
            matchingInput.focus();
          } else {
            // Fallback to summary/close button if the checkbox can't be found
            const fallbackSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
              ? `.mobile-facets__close-button`
              : `.facets__summary`;
            const fallbackElement = newFacetDetailsElement.querySelector(fallbackSelector);
            if (fallbackElement) fallbackElement.focus();
          }
        }
      }
    }
    // Restore after Dawn rewrites the clicked filter HTML (server may not mark active_values).
    FacetFiltersForm.restoreMerooCatalogState(window.location.search);
    document.querySelectorAll('facet-filters-form form').forEach(FacetFiltersForm.syncCatalogSelections);
  }

  static syncCatalogCheckboxesFromUrl(searchParams = window.location.search) {
    const params = new URLSearchParams(
      searchParams.startsWith('?') ? searchParams.slice(1) : searchParams
    );
    const selected = params.getAll('filter.p.product_type');
    if (!selected.length) return false;
    const selectedSet = new Set(selected);
    document.querySelectorAll('input.meroo-catalog-filter__checkbox[name="filter.p.product_type"]').forEach((input) => {
      input.checked = selectedSet.has(input.value);
      input.closest('.meroo-catalog-filter__link')?.classList.toggle('is-active', input.checked);
    });
    document.querySelectorAll('facet-filters-form form').forEach(FacetFiltersForm.syncCatalogSelections);
    return true;
  }

  static applyMerooCatalogTypeFilter(searchParams = window.location.search) {
    const grid = document.getElementById('product-grid');
    if (!grid || !document.querySelector('[data-meroo-catalog-grid]')) return;

    const params = new URLSearchParams(
      searchParams.startsWith('?') ? searchParams.slice(1) : searchParams
    );
    const selected = params
      .getAll('filter.p.product_type')
      .map((value) => value.trim())
      .filter(Boolean);
    const selectedLower = new Set(selected.map((value) => value.toLowerCase()));
    const items = [...grid.querySelectorAll('.meroo-collection__item[data-product-type]')];
    if (!items.length) return;

    let visible = 0;
    items.forEach((item) => {
      const type = (item.dataset.productType || '').trim();
      const match = !selectedLower.size || selectedLower.has(type.toLowerCase());
      item.hidden = !match;
      item.classList.toggle('is-filter-hidden', !match);
      if (match) visible += 1;
    });

    const total =
      Number(document.getElementById('ProductCountDesktop')?.dataset.totalCount) ||
      Number(document.getElementById('ProductCount')?.dataset.totalCount) ||
      items.length;
    const countLabel = selected.length
      ? `${visible} of ${total} products`
      : `${total} products`;
    ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      el.dataset.productCount = String(selected.length ? visible : total);
      if (!el.dataset.totalCount) el.dataset.totalCount = String(total);
      el.textContent = countLabel;
      el.classList.remove('loading');
    });

    const results = grid.closest('.meroo-collection__results');
    if (results) results.hidden = selected.length > 0 && visible === 0;
  }

  static restoreMerooCatalogState(searchParams = window.location.search) {
    FacetFiltersForm.syncCatalogCheckboxesFromUrl(searchParams);
    FacetFiltersForm.applyMerooCatalogTypeFilter(searchParams);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const element = document.querySelector(selector);
      const restoreSortFocus = element.contains(document.activeElement) &&
        document.activeElement.hasAttribute('data-meroo-sort-trigger');
      element.innerHTML = html.querySelector(selector).innerHTML;
      if (restoreSortFocus) element.querySelector('[data-meroo-sort-trigger]')?.focus({ preventScroll: true });
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams, pathname = window.location.pathname) {
    history.pushState({ searchParams }, '', `${pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    const form = event.target.closest('form');
    const catalogForm = form?.dataset.merooCatalogBaseUrl
      ? form
      : document.getElementById('FacetFiltersForm');
    // Legacy /solid and /functional tag paths would AND with the selected
    // product types. Move to the collection's base path before applying OR.
    const pathname = (
      catalogForm?.dataset.merooCatalogBaseUrl ||
      window.location.pathname
    ).trim();
    FacetFiltersForm.renderPage(searchParams, event, true, pathname);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sourceForm = event.target.closest('form');
    if (!sourceForm) return;
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const forms = [];
    const isMobile = sourceForm.id === 'FacetFiltersFormMobile';

    sortFilterForms.forEach((form) => {
      if (!isMobile) {
        if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
          forms.push(this.createSearchParams(form));
        }
      } else if (form.id === 'FacetFiltersFormMobile') {
        forms.push(this.createSearchParams(form));
      }
    });
    this.onSubmitForm(forms.join('&'), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    // Tag categories use collection paths. Clearing them requires a new page,
    // while native price and availability changes keep using section rendering.
    const targetURL = new URL(event.currentTarget.href);
    if (targetURL.pathname !== window.location.pathname) {
      window.location.assign(targetURL.href);
      return;
    }
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.requestId = 0;
FacetFiltersForm.submitTimeout = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.pathnamePrev = window.location.pathname;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => FacetFiltersForm.restoreMerooCatalogState(), { once: true });
} else {
  FacetFiltersForm.restoreMerooCatalogState();
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    if (this.hasAttribute('data-meroo-price-range')) return;
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  connectedCallback() {
    if (!this.hasAttribute('data-meroo-price-range') || this.merooSlider) return;
    this.merooSlider = this.querySelector('[data-meroo-price-slider]');
    if (!this.merooSlider) return;

    this.merooMaximum = Math.max(0, Number(this.merooSlider.dataset.rangeMax) || 0);
    this.merooBounds = {};
    for (const bound of ['min', 'max']) {
      const range = this.querySelector(`[data-meroo-price-bound="${bound}"]`);
      const edit = this.querySelector(`[data-meroo-price-edit="${bound}"]`);
      const value = this.querySelector(`[data-meroo-price-value="${bound}"]`);
      this.merooBounds[bound] = { range, edit, value };
      range.addEventListener('input', () => this.syncMerooValues(bound, range.value));
      edit.addEventListener('change', () => this.syncMerooValues(bound, edit.value));
      edit.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        edit.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    try {
      this.merooMoney = new Intl.NumberFormat(this.merooSlider.dataset.locale || document.documentElement.lang, {
        style: 'currency',
        currency: this.merooSlider.dataset.currency,
        minimumFractionDigits: 0,
      });
    } catch {
      this.merooMoney = { format: (value) => `${this.merooSlider.dataset.currency} ${value}` };
    }
    this.syncMerooValues();
  }

  syncMerooValues(changedBound, requestedValue) {
    const { min, max } = this.merooBounds;
    const limit = this.merooMaximum;
    const clamp = (value) => Math.round(Math.min(limit, Math.max(0, Number(value) || 0)) * 100) / 100;
    let lower = clamp(min.range.value);
    let upper = clamp(max.range.value);

    if (changedBound === 'min') {
      lower = Math.min(clamp(requestedValue), upper);
    } else if (changedBound === 'max') {
      upper = Math.max(requestedValue === '' ? limit : clamp(requestedValue), lower);
    } else {
      lower = Math.min(lower, upper);
    }

    for (const [bound, amount] of [['min', lower], ['max', upper]]) {
      const controls = this.merooBounds[bound];
      const isUnbounded = bound === 'min' ? amount === 0 : amount === limit;
      controls.range.value = String(amount);
      controls.range.disabled = limit === 0;
      controls.range.setAttribute('aria-valuetext', this.merooMoney.format(amount));
      controls.edit.value = String(amount);
      controls.value.value = isUnbounded ? '' : String(amount);
      controls.value.disabled = isUnbounded;
    }

    min.range.setAttribute('aria-valuemax', String(upper));
    max.range.setAttribute('aria-valuemin', String(lower));
    min.edit.max = String(upper);
    max.edit.min = String(lower);
    min.range.style.zIndex = lower === limit ? '4' : '2';
    this.merooSlider.style.setProperty('--meroo-range-start', `${limit ? (lower / limit) * 100 : 0}%`);
    this.merooSlider.style.setProperty('--meroo-range-end', `${limit ? (upper / limit) * 100 : 0}%`);
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
